import { useEffect, useState } from 'react';

import { cx } from '../cx';
import { moduleOrigins } from '../modules';
import { Container } from './Layout';

/** Um logotipo do carrossel: `logoSrc` ja pronto a usar (absoluto fora do site). */
export type PartnerLogo = {
  id: string;
  name: string;
  logoSrc: string;
  /** Vazio = sem ligacao. */
  websiteUrl: string;
};

/** Abaixo disto os logotipos ficam parados, centrados: nao chegam para encher a faixa. */
const MIN_TO_SCROLL = 5;

function Logo({ partner, hidden = false }: { partner: PartnerLogo; hidden?: boolean }) {
  const img = (
    <img
      src={partner.logoSrc}
      alt={hidden ? '' : partner.name}
      loading="lazy"
      className="gu-partner-logo h-10 w-auto max-w-[10rem] object-contain"
    />
  );
  if (!partner.websiteUrl) {
    return <span className="flex shrink-0 items-center">{img}</span>;
  }
  return (
    <a
      href={partner.websiteUrl}
      target="_blank"
      rel="sponsored noopener noreferrer"
      tabIndex={hidden ? -1 : undefined}
      className="flex shrink-0 items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus"
    >
      {img}
    </a>
  );
}

/**
 * A faixa de parceiros por cima do rodape publico (site e Stream Portal):
 * logotipos em branco, a cores com o rato por cima, a deslizar devagar. Para
 * com o rato ou o foco do teclado; com "reduzir movimento" fica parada e
 * percorre-se a mao. Ver docs/conteudos.
 */
export function PartnersBand({ partners, allHref }: { partners: PartnerLogo[]; allHref: string }) {
  if (partners.length === 0) return null;
  const scrolls = partners.length >= MIN_TO_SCROLL;
  return (
    <section aria-label="Parceiros" className="border-t border-gu-line bg-gu-raised py-5">
      <Container className="flex flex-wrap items-center gap-x-8 gap-y-3">
        <p className="font-sub text-label font-bold uppercase text-gu-ink-muted">Parceiros</p>
        <a
          href={allHref}
          className="gu-link order-last text-caption sm:order-none sm:ml-auto md:order-last"
        >
          Ver todos os parceiros
        </a>
        <div
          className={cx(
            'gu-marquee-wrap w-full overflow-hidden md:w-auto md:flex-1',
            scrolls && 'gu-marquee-fade',
          )}
        >
          <div
            className={cx(
              'flex w-max items-center gap-12',
              scrolls ? 'gu-marquee' : 'mx-auto flex-wrap justify-center',
            )}
            style={
              scrolls
                ? { ['--gu-marquee-duration' as string]: `${String(partners.length * 4)}s` }
                : undefined
            }
          >
            {partners.map((p) => (
              <Logo key={p.id} partner={p} />
            ))}
            {/* A segunda volta, para o fim encostar ao inicio sem salto. */}
            {scrolls
              ? partners.map((p) => (
                  <span key={`${p.id}-2`} aria-hidden="true" className="gu-marquee-copy flex">
                    <Logo partner={p} hidden />
                  </span>
                ))
              : null}
          </div>
        </div>
      </Container>
    </section>
  );
}

/**
 * Os parceiros do carrossel, lidos da API do site (`GET /api/parceiros`,
 * aberta a outros dominios). Para o Stream Portal; o site usa a lista que ja
 * tem. Com a API em baixo, sem faixa.
 */
export function useSitePartnerLogos(siteOrigin = moduleOrigins().site): PartnerLogo[] {
  const [logos, setLogos] = useState<PartnerLogo[]>([]);
  useEffect(() => {
    let alive = true;
    fetch(`${siteOrigin}/api/parceiros`)
      .then((res) => (res.ok ? (res.json() as Promise<unknown>) : []))
      .then((body) => {
        if (alive) setLogos(toPartnerLogos(body, siteOrigin));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [siteOrigin]);
  return logos;
}

/**
 * Da resposta de `/api/parceiros` aos logotipos do carrossel: so os marcados
 * para o carrossel, com o logotipo pequeno e o endereco do site a frente.
 */
export function toPartnerLogos(body: unknown, siteOrigin = ''): PartnerLogo[] {
  if (!Array.isArray(body)) return [];
  return body.flatMap((item: unknown) => {
    if (typeof item !== 'object' || item === null) return [];
    const p = item as Record<string, unknown>;
    if (
      p.inCarousel !== true ||
      typeof p.id !== 'string' ||
      typeof p.name !== 'string' ||
      typeof p.logoUrl !== 'string' ||
      !p.logoUrl.startsWith('/api/media/')
    ) {
      return [];
    }
    const websiteUrl =
      typeof p.websiteUrl === 'string' && p.websiteUrl.startsWith('https://') ? p.websiteUrl : '';
    const small = p.logoUrl.replace(/\/(md|lg)\.webp$/, '/sm.webp');
    return [{ id: p.id, name: p.name, logoSrc: `${siteOrigin}${small}`, websiteUrl }];
  });
}
